import {
  AdminAlertContentResponseAlert,
  AdminAlertHitResponseAlertHit,
  AdminRiskModelConfigRow,
  AdminRiskModelExecutionResponseRow,
  AdminRiskModelResponseRow,
  AdminRuleContentResponseRule,
  AdminRuleExecutionResponseRuleExecution,
  AdminRuleQueryResponseRow,
  AdminRuleScheduleResponseRuleSchedule,
  AdminRuleValidationResponseRow,
  AdminScenarioConfigsResponseRow,
} from 'app/modules/rulesAdmin/types/responses';
import { U21SelectOptionProps } from 'app/shared/u21-ui/components';
import {
  RiskModelExecutionStatus,
  RiskModelExecutionType,
  RiskModelStatus,
} from 'app/modules/riskRatings/models';

export enum ExecutionEngine {
  POSTGRES = 'POSTGRES',
  POSTGRES_READER = 'POSTGRES_READER',
  SNOWFLAKE = 'SNOWFLAKE',
  DELTALAKE = 'DELTALAKE',
}

export const EXECUTION_ENGINE_OPTIONS: U21SelectOptionProps<ExecutionEngine>[] =
  [
    {
      text: 'Postgres',
      value: ExecutionEngine.POSTGRES,
    },
    {
      text: 'Postgres reader',
      value: ExecutionEngine.POSTGRES_READER,
    },
    {
      text: 'Snowflake',
      value: ExecutionEngine.SNOWFLAKE,
    },
    {
      text: 'Delta lake',
      value: ExecutionEngine.DELTALAKE,
    },
  ];

export const DB_TIMEOUT_KEYS: U21SelectOptionProps<ExecutionEngine>[] = [
  {
    text: 'Postgres',
    value: ExecutionEngine.POSTGRES,
  },
  {
    text: 'Snowflake',
    value: ExecutionEngine.SNOWFLAKE,
  },
  {
    text: 'Delta lake',
    value: ExecutionEngine.DELTALAKE,
  },
];

export const EXECUTION_ENGINE_OPTIONS_WITHOUT_SNOWFLAKE: U21SelectOptionProps<ExecutionEngine>[] =
  [
    {
      text: 'Postgres',
      value: ExecutionEngine.POSTGRES,
    },
    {
      text: 'Postgres reader',
      value: ExecutionEngine.POSTGRES_READER,
    },
  ];

export enum ExecutionType {
  MANUAL = 'MANUAL',
  MAKEUP = 'MAKEUP',
  UPLOAD = 'UPLOAD',
  SCHEDULE = 'SCHEDULE',
}

export const EXECUTION_TYPE_OPTIONS: U21SelectOptionProps<ExecutionType>[] = [
  {
    text: 'Manual',
    value: ExecutionType.MANUAL,
  },
  {
    text: 'Makeup',
    value: ExecutionType.MAKEUP,
  },
  {
    text: 'Upload',
    value: ExecutionType.UPLOAD,
  },
  {
    text: 'Schedule',
    value: ExecutionType.SCHEDULE,
  },
];

export enum ScheduleType {
  MAKEUP = 'MAKEUP',
  SCHEDULE = 'SCHEDULE',
}

export const SCHEDULE_TYPE_OPTIONS: U21SelectOptionProps<ScheduleType>[] = [
  {
    text: 'Makeup',
    value: ScheduleType.MAKEUP,
  },
  {
    text: 'Schedule',
    value: ScheduleType.SCHEDULE,
  },
];

export enum ScheduleStatus {
  WAITING = 'WAITING',
  STARTED = 'STARTED',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
  CANCELLED = 'CANCELLED',
  ERROR_EXPECTED = 'ERROR_EXPECTED',
}

export const SCHEDULE_STATUS_OPTIONS: U21SelectOptionProps<ScheduleStatus>[] = [
  {
    text: 'Waiting',
    value: ScheduleStatus.WAITING,
  },
  {
    text: 'Started',
    value: ScheduleStatus.STARTED,
  },
  {
    text: 'Complete',
    value: ScheduleStatus.COMPLETE,
  },
  {
    text: 'Error',
    value: ScheduleStatus.ERROR,
  },
  {
    text: 'Cancelled',
    value: ScheduleStatus.CANCELLED,
  },
  {
    text: 'Error Expected',
    value: ScheduleStatus.ERROR_EXPECTED,
  },
];

export const SCENARIO_OPTIONS: U21SelectOptionProps<string>[] = [
  {
    text: 'Simple Object Count (Transactions)',
    value: 'simple_object_count_transactions_embedded_filters',
  },
  {
    text: 'Simple Object Count (Entities/Instruments)',
    value: 'simple_object_count_transactions',
  },
  {
    text: 'Same value transactions',
    value: 'consecutive_transactions_embedded_filters',
  },
  {
    text: 'Top Transacting Entities',
    value: 'top_transacting_entities_embedded_filters',
  },
  {
    text: 'Newly Seen',
    value: 'newly_seen_embedded_filters',
  },
  {
    text: 'Historical Deviation B',
    value: 'historical_deviations_b_embedded_filters',
  },
  {
    text: 'Historical Deviation A',
    value: 'historical_deviations_a_embedded_filters',
  },
  {
    text: 'Check Rules',
    value:
      'tardy_check, serial_number_out_of_range, check_matches_recent_deposit, missing_serial_number, duplicate_serial_number',
  },
  {
    text: 'Entity Specific Conduit',
    value: 'entity_specific_conduit_embedded_filters',
  },
  {
    text: 'Pass-Through Transferred Percent',
    value: 'pass_through_transferred_percent_embedded_filters',
  },
  {
    text: 'Transaction Statistic A',
    value: 'transaction_statistics_a_embedded_filters',
  },
  {
    text: 'Pass Through',
    value: 'pass_through_embedded_filters',
  },
  {
    text: 'Layering',
    value: 'layering_embedded_filters',
  },
  {
    text: 'Alerted Transactions II',
    value: 'alerted_transactions_ii_embedded_filters',
  },
  {
    text: 'Multiple Occurrences',
    value: 'multiple_occurrences',
  },
  {
    text: 'Watchlist',
    value: 'watchlist',
  },
  {
    text: 'IP Blacklist (Global)',
    value: 'ip_blacklist_global_embedded_filters',
  },
  {
    text: 'Blacklist String (Events)',
    value: 'event_blacklist_string_embedded_filters',
  },
  {
    text: 'Blacklist String (Entities/Instruments)',
    value: 'entity_blacklist_string_embedded_filters',
  },
  {
    text: 'Sequence Rolling Window',
    value: 'sequence_rolling_window',
  },
  {
    text: 'Sequence Cumulative Aggregate',
    value: 'sequence_cumulative_aggregate',
  },
  {
    text: 'Sequence Cumulative Aggregate USPS',
    value: 'sequence_cumulative_aggregate_usps',
  },
  {
    text: 'Sequence Daily Aggregate',
    value: 'sequence_daily_aggregate',
  },
  {
    text: 'Sequence Rolling Window One Step',
    value: 'sequence_rolling_window_one_step',
  },
  {
    text: 'Sequence Rolling Window With Count',
    value: 'sequence_rolling_window_with_count',
  },
  {
    text: 'Sequence Rolling Window Round Trip',
    value: 'sequence_rolling_window_round_trip',
  },
  {
    text: 'Simple Filters',
    value: 'simple_filters',
  },
  {
    text: 'Graph Based Rule',
    value: 'entity_link_counts',
  },
  {
    text: 'Consortium Rules',
    value:
      'consortium_new_account_monitoring, consortium_ongoing_account_monitoring, consortium_flagged_ach_originator_or_receiver',
  },
  {
    text: 'Dormant Activity',
    value: 'dormant_activity_embedded_filters',
  },
  {
    text: 'ACH Risk Score Rule',
    value: 'ach_risk_score',
  },
  {
    text: 'Enhance Due Diligence - Periodic Review',
    value: 'edd_periodic_review',
  },
  {
    text: 'Simple Statistics',
    value: 'simple_statistics_embedded_filters',
  },
  {
    text: 'Simple Statistics With Count',
    value: 'simple_statistics_with_count_embedded_filters',
  },
  {
    text: 'Simple Count',
    value: 'simple_count_embedded_filters',
  },
  {
    text: 'Structuring',
    value: 'structuring_embedded_filters',
  },
  {
    text: 'ACH Rules',
    value:
      'duplicate_ach_transaction, ach_mismatched_account_info, multiple_failed_validation_attempts',
  },
  {
    text: '[GREENDOT ONLY] - H10 Internal Transfers - Dispersion and Aggregation Sequence',
    value: 'dispersion_and_aggregation_sequence_embedded_filters',
  },
  {
    text: '[GREENDOT ONLY] - H5/15/16 Counterparty Concentration (Per Segment)',
    value: 'counterparty_concentration_directional_embedded_filters',
  },
];

export enum ValidationStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETE = 'COMPLETE',
  GENERATING_VALIDATION_ALERTS = 'GENERATING_VALIDATION_ALERTS',
  QUERY_TIMEOUT = 'QUERY_TIMEOUT',
  FAILED = 'FAILED',
}

export const VALIDATION_STATUS_OPTIONS: U21SelectOptionProps<ValidationStatus>[] =
  [
    {
      text: 'Pending',
      value: ValidationStatus.PENDING,
    },
    {
      text: 'Running',
      value: ValidationStatus.RUNNING,
    },
    {
      text: 'Complete',
      value: ValidationStatus.COMPLETE,
    },
    {
      text: 'Generating validation alerts',
      value: ValidationStatus.GENERATING_VALIDATION_ALERTS,
    },
    {
      text: 'Query timeout',
      value: ValidationStatus.QUERY_TIMEOUT,
    },
    {
      text: 'Failed',
      value: ValidationStatus.FAILED,
    },
  ];

export const RISK_MODEL_EXECUTION_STATUS_OPTIONS: U21SelectOptionProps<RiskModelExecutionStatus>[] =
  [
    {
      text: 'Queued',
      value: RiskModelExecutionStatus.QUEUED,
    },
    {
      text: 'In progress',
      value: RiskModelExecutionStatus.IN_PROGRESS,
    },
    {
      text: 'Success',
      value: RiskModelExecutionStatus.SUCCESS,
    },
    {
      text: 'Failed',
      value: RiskModelExecutionStatus.FAILED,
    },
    {
      text: 'Canceled',
      value: RiskModelExecutionStatus.CANCELED,
    },
  ];

export const RISK_MODEL_EXECUTION_TYPE_OPTIONS: U21SelectOptionProps<RiskModelExecutionType>[] =
  [
    {
      text: 'Weekly scoring',
      value: RiskModelExecutionType.WEEKLY_SCORE,
    },
    {
      text: 'Unscored entities',
      value: RiskModelExecutionType.UNSCORED_ENTITIES,
    },
    {
      text: 'Validation',
      value: RiskModelExecutionType.VALIDATION,
    },
  ];

export const RISK_MODEL_STATUS_OPTIONS: U21SelectOptionProps<string>[] = [
  {
    text: 'Draft',
    value: RiskModelStatus.DRAFT,
  },
  {
    text: 'Active',
    value: RiskModelStatus.ACTIVE,
  },
  {
    text: 'Deactivated',
    value: RiskModelStatus.DEACTIVATED,
  },
];

export enum AlertStatus {
  OPEN = 'OPEN',
  OPEN_SHADOW = 'OPEN_SHADOW',
  CLOSED = 'CLOSED',
  RULE_VALIDATION = 'RULE_VALIDATION',
}

export const ALERT_STATUS_OPTIONS: U21SelectOptionProps<AlertStatus>[] = [
  {
    text: 'Open',
    value: AlertStatus.OPEN,
  },
  {
    text: 'Open shadow',
    value: AlertStatus.OPEN_SHADOW,
  },
  {
    text: 'Closed',
    value: AlertStatus.CLOSED,
  },
  {
    text: 'Rule validation',
    value: AlertStatus.RULE_VALIDATION,
  },
];

export enum AlertType {
  TM = 'TM',
  KYC = 'KYC',
  CAR = 'CAR',
  CHAINALYSIS = 'CHAINALYSIS',
  MANUAL = 'MANUAL',
  TRM = 'TRM',
}

export const ALERT_TYPE_OPTIONS: U21SelectOptionProps<AlertType>[] = [
  {
    text: 'Transaction monitoring (TM)',
    value: AlertType.TM,
  },
  {
    text: 'Know your customer (KYC)',
    value: AlertType.KYC,
  },
  {
    text: 'Continuing activity reports (CAR)',
    value: AlertType.CAR,
  },
  {
    text: 'Chainalysis',
    value: AlertType.CHAINALYSIS,
  },
  {
    text: 'Manual',
    value: AlertType.MANUAL,
  },
  {
    text: 'TRM',
    value: AlertType.TRM,
  },
];

export type RuleAdminResponseType =
  | AdminScenarioConfigsResponseRow
  | AdminRuleValidationResponseRow
  | AdminRuleExecutionResponseRuleExecution
  | AdminRuleQueryResponseRow
  | AdminRuleContentResponseRule
  | AdminRuleScheduleResponseRuleSchedule
  | AdminRiskModelExecutionResponseRow
  | AdminRiskModelResponseRow
  | AdminRiskModelConfigRow
  | AdminAlertContentResponseAlert
  | AdminAlertHitResponseAlertHit;

export enum RiskRatingsCeleryTask {
  COMPUTE_WEEKLY_RISK_RATINGS_FOR_ORG = 'Compute weekly risk ratings for org',
  COMPUTE_DAILY_RISK_RATINGS_FOR_ORG = 'Compute daily risk ratings for org',
  LAUNCH_SPARK_CRR_JOB = 'Launch Spark CRR job',
  CANCEL_SPARK_CRR_EXECUTION = 'Cancel Spark CRR execution',
}

export enum RulesAdminModalType {
  NONE = '',
  UPDATE_SCHEDULE_STATUS = 'UPDATE_SCHEDULE_STATUS',
  UPDATE_RETRY_COUNT = 'UPDATE_RETRY_COUNT',
  UPDATE_TIMEOUT = 'UPDATE_TIMEOUT',
  UPDATE_VALIDATION_STATE = 'UPDATE_VALIDATION_STATE',
  UPDATE_SCENARIO_CONFIG = 'UPDATE_SCENARIO_CONFIG',
  UPDATE_RISK_MODEL_EXECUTION_STATUS = 'UPDATE_RISK_MODEL_EXECUTION_STATUS',
}

type RulesAdminWriteConfig =
  | {
      title: string;
      desc: string;
      type: 'dropdown';
      options: U21SelectOptionProps[];
      label: string;
    }
  | {
      title: string;
      desc: string;
      type: 'input';
      label: string;
    }
  | {
      title: string;
      desc: string;
      type: 'buttonGroup';
      positiveLabel: string;
      negativeLabel: string;
    }
  | {
      title: string;
      desc: string;
      type: 'checkbox';
      label: string;
    };

export const RULE_ADMIN_WRITE_CONFIG: Record<
  RulesAdminModalType,
  RulesAdminWriteConfig
> = {
  [RulesAdminModalType.NONE]: {
    title: '',
    desc: '',
    type: 'dropdown',
    options: [],
    label: '',
  },
  [RulesAdminModalType.UPDATE_SCHEDULE_STATUS]: {
    title: 'Set Schedule Status',
    desc: 'Set the schedule status for the selected rule schedules:',
    type: 'dropdown',
    options: SCHEDULE_STATUS_OPTIONS,
    label: 'Schedule status',
  },
  [RulesAdminModalType.UPDATE_RETRY_COUNT]: {
    title: 'Set Retry Count',
    desc: 'Set the retry count for the selected rule schedules:',
    type: 'input',
    label: 'Retry count',
  },
  [RulesAdminModalType.UPDATE_TIMEOUT]: {
    title: 'Set Timeout',
    desc: 'Set the timeout for the selected rules:',
    type: 'input',
    label: 'Timeout (in Minutes)',
  },
  [RulesAdminModalType.UPDATE_VALIDATION_STATE]: {
    title: 'Set Validation State',
    desc: 'Set whether rules are synchronous for the selected rules:',
    type: 'dropdown',
    options: VALIDATION_STATUS_OPTIONS,
    label: 'Validation status',
  },
  [RulesAdminModalType.UPDATE_SCENARIO_CONFIG]: {
    title: 'Manage enabled scenarios',
    desc: 'Enable scenarios for the org(s)',
    type: 'dropdown',
    label: 'Scenario',
    options: SCENARIO_OPTIONS,
  },
  [RulesAdminModalType.UPDATE_RISK_MODEL_EXECUTION_STATUS]: {
    title: 'Set Risk Model Execution Status',
    desc: 'Set the risk model execution status for the selected risk model executions:',
    type: 'dropdown',
    options: RISK_MODEL_EXECUTION_STATUS_OPTIONS,
    label: 'Risk model execution status',
  },
};

export const RISK_MODEL_PARAMETER_OPTIONS = [
  { value: 'GEOGRAPHIC_MATCH', text: 'Geographic match' },
  { value: 'AGE_OF_RECORD', text: 'Age of record' },
  { value: 'ENTITY_INFORMATION_MATCH', text: 'Entity information match' },
  { value: 'SAR_VOLUME', text: 'SAR volume' },
  { value: 'ARTICLE_VOLUME', text: 'Article volume' },
  { value: 'RULE_MATCH', text: 'Rule match' },
  { value: 'ENTITY_CUSTOM_DATA_MATCH', text: 'Entity custom data match' },
];

export const RISK_METRIC_TYPE_OPTIONS = [
  { value: 'TEXT', text: 'Text' },
  { value: 'NUMBER', text: 'Number' },
  { value: 'BOOLEAN', text: 'Boolean' },
];

export const RISK_METRIC_MAPPING_METHOD_OPTIONS = [
  { value: 'STRING_MATCH', text: 'String match' },
  { value: 'MATCHLIST', text: 'Matchlist' },
  { value: 'STRING_LIST_MATCH', text: 'String list match' },
  { value: 'NUMERIC_COMPARISON', text: 'Numeric comparison' },
  { value: 'SCALE_NUMBER', text: 'Scale number' },
  { value: 'BOOLEAN_MATCH', text: 'Boolean match' },
];

export interface RulesAdminInitialState {
  rulesAdminModalType: RulesAdminModalType;
}
